import {
    Logger, Matrix, namespace, Point,
} from '../../../../../FrontendBundle/Resources/js/ofcs';
import AbstractItem from './abstractItem';

namespace('Ofcs.Editor.Item');

/**
 * @class TablelegItem
 * @extends AbstractItem
 */
export default window.Ofcs.Editor.Item.TablelegItem = AbstractItem.extend({
    type: 'tableleg',

    /**
     * Constant for minimum height of a tableleg bag
     *
     * @constant
     * @property {number} MIN_HEIGHT
     */
    MIN_HEIGHT: 475,

    /**
     * Constant for maximum width of a tableleg bag
     *
     * @constant
     * @property {number} MAX_WIDTH
     */
    MAX_WIDTH: 50,

    /**
     * Allowed sizes for the tableleg item
     *
     * @property {Array.<number>} defaultSizes
     */
    defaultSizes: [475, 700],

    /**
     * Triggered when the segments are updated
     *
     * @event updatesegments
     * @param {TablelegItem} item Me
     */
    defaults: {
        /**
         * @property {string} Type
         */
        type: 'Tableleg',

        /**
         * Current position
         * @property {Point} attributes.position
         */
        position: new Point(0, 0),

        /**
         * Current rotation
         * @property {number} attributes.rotation
         */
        rotation: 0,

        /**
         * Width of the shape
         * @property {number} attributes.width
         */
        width: 50,

        /**
         * Height of the shape
         * @property {number} attributes.height
         */
        height: 700,

        /**
         * Label
         * @property {string} attributes.userLabel
         */
        userLabel: '',

        /**
         * Current snap index
         * @see Ofcs.Editor.Svg.Handle.ResizeTablelegHandle
         * @property {string} attributes.snapIndex
         */
        snapIndex: 1,

        /**
         * Defines it item is billable
         * Will be set and checked by tabletopCollisionManager
         * @property {Boolean} attributes.billable
         */
        billable: false,
    },

    initialize() {
        AbstractItem.prototype.initialize.call(this);

        this.supportsHeight = true;
        this.supportsRotation = true;
        this.supportsPositioning = true;
        this.supportsDepth = false;
        this.supportsOutOfFoam = true;
        this.supportsCollisions = true;
        this.supportsBidirectionalCollisions = false;
        this.supportsMarginCollisions = false;
        this.supportsInsideTable = 'inside';
        this.supportsGuides = true;

        // Initialize paths
        this.set('ext', this.calculateSegments(0));
        this.set('orig', this.get('ext'));
        this.set('safety', this.calculateSegments(0));

        this.on('change', this.onItemChange, this);
    },

    /**
     * Return safety margin for this item
     *
     * @return {number}
     */
    getSafetyMargin() {
        // signal maximum of possible margins to collisionManager to find adequate leader in case of collision
        return 0;
    },

    onItemChange(item) {
        const changes = item.changed;

        if (changes.height !== undefined) {
            this.set('ext', this.calculateSegments(0));
            this.set('orig', this.get('ext'));
            this.set('safety', this.calculateSegments(0));
            this.trigger('updatesegments', this);
        }

        if (changes.position !== undefined && changes.height === undefined) {
            this.set('safety', this.calculateSegments(0));
            this.trigger('updatesegments', this);
        }
    },

    calculateSegments(spreadParam) {
        const spread = {
            x: spreadParam,
            y: spreadParam,
        };

        const width = this.get('width') + (spread.x * 2);
        const height = this.get('height') + (spread.y * 2);
        const path = [];

        path.push(['M', -width / 2, -height / 2]);
        path.push(['L', width / 2, -height / 2]);
        path.push(['L', width / 2, height / 2]);
        path.push(['L', -width / 2, height / 2]);
        path.push(['L', -width / 2, -height / 2]);

        return [path];
    },

    set(key, value, optionsParam) {
        let attrs = {};
        let options = optionsParam;

        // Handle both `"key", value` and `{key: value}` -style arguments.
        if (typeof key === 'object') {
            attrs = key;
            options = value;
        } else {
            attrs[key] = value;
        }

        options = options || {};

        if (attrs.height !== undefined) {
            attrs.height = Math.max(this.MIN_HEIGHT, attrs.height);
            Logger.debug('tableleg: fixed height', attrs.height);
        }

        // tablelegs width is always 80
        if (attrs.width !== undefined) {
            attrs.width = this.MAX_WIDTH;
            Logger.debug('tableleg: fixed width', this.MAX_WIDTH);
        }

        return AbstractItem.prototype.set.call(this, key, value, options);
    },

    /**
     * Get margin factor for dynamic margin calculation
     *
     * @return {number} factor for margin calculation
     */
    getMarginFactor() {
        return 0;
    },

    /**
     * Get top-middle point respect rotation.
     *
     * @return {Point}
     */
    getPointTop() {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const height = this.get('height');
        const point = new Point(center.x, center.y - (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get bottom-middle point respect rotation.
     *
     * @return {Point}
     */
    getPointBottom() {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const height = this.get('height');
        const point = new Point(center.x, center.y + (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get middle-left point respect rotation.
     *
     * @return {Point}
     */
    getPointLeft() {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = this.get('width');
        const point = new Point(center.x - (width / 2), center.y);

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get middle-right point respect rotation.
     *
     * @return {Point}
     */
    getPointRight() {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = this.get('width');
        const point = new Point(center.x + (width / 2), center.y);

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get top-left point respect rotation.
     *
     * @return {Point}
     */
    getPointTopLeft() {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = this.get('width');
        const height = this.get('height');
        const point = new Point(center.x - (width / 2), center.y - (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get top-right point respect rotation.
     *
     * @return {Point}
     */
    getPointTopRight() {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = this.get('width');
        const height = this.get('height');
        const point = new Point(center.x + (width / 2), center.y - (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get bottom-left point respect rotation.
     *
     * @return {Point}
     */
    getPointBottomLeft() {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = this.get('width');
        const height = this.get('height');
        const point = new Point(center.x - (width / 2), center.y + (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get bottom-right point respect rotation.
     *
     * @return {Point}
     */
    getPointBottomRight() {
        const matrix = new Matrix();
        const center = this.get('position');
        const rotation = this.get('rotation');
        const width = this.get('width');
        const height = this.get('height');
        const point = new Point(center.x + (width / 2), center.y + (height / 2));

        matrix.rotate(rotation, center);
        point.transform(matrix);

        return point;
    },

    /**
     * Get minimum height.
     *
     * @return {number}
     */
    getMinHeight() {
        return this.MIN_HEIGHT;
    },

    /**
     * Get the area of the item
     *
     * @return {number}
     */
    getArea() {
        return this.get('width') * this.get('height');
    },

    /**
     * Translate object into json stringable types. This method will be used
     * by JSON.stringify() automaticly.
     *
     * @return {Object}
     */
    toJSON(...args) {
        const attributes = AbstractItem.prototype.toJSON.apply(this, args);
        return {
            x: attributes.x,
            y: attributes.y,
            rotation: attributes.rotation,
            width: attributes.width,
            height: attributes.height,
            type: attributes.type,
            billable: attributes.billable,
        };
    },

    /**
     * Initializes item handles
     *
     * @param {Ofcs.Editor.Svg.Canvas} canvas - svg canvas instance
     * @return {Ofcs.Editor.Svg.Handle.ResizeTablelegHandle}
     */
    initializeHandles(canvas) {
        return new Ofcs.Editor.Svg.Handle.ResizeTablelegHandle(this, canvas);
    },
});
